import { createMemo } from "solid-js";
import { useI18n } from "@/providers/i18n-provider";
import { type ConnectivityContext, getConnectivityDescriptionKeys } from "@bso/shared";
import { getConnectivityUI } from "@/lib/powersync/connectivity-ui";

/**
 * Hook to get unified connectivity dialog content.
 * Centralizes labels and messages for all platforms.
 */
export function useConnectivityDialog(
  stateValue: () => string | object,
  context: () => ConnectivityContext,
) {
  const { t } = useI18n();
  const ui = createMemo(() => getConnectivityUI(stateValue(), context()));

  const dialogContent = createMemo(() => {
    const ctx = context();
    const keys = getConnectivityDescriptionKeys(ui().labelKey, ctx);

    const content = (() => {
      // Map shared keys to actual translated strings.
      // Note: These keys must be present in the i18n catalogs.
      switch (keys.titleKey) {
        case "connectivity.offline.title": {
          return {
            title: t("You're Offline"),
            message: t("Check your connection to sync your changes."),
            confirmText: t("Check Again"),
          };
        }
        case "connectivity.reconnecting.title": {
          return {
            title: t("Server Offline"),
            message: t("We're having trouble reaching the server. We'll keep trying..."),
            confirmText: t("Check Again"),
          };
        }
        case "connectivity.maintenance.title": {
          return {
            title: t("Maintenance"),
            message: ctx.maintenanceMessage ?? t("The server is down for maintenance."),
            confirmText: t("Check Again"),
          };
        }
        case "connectivity.unauthenticated.title": {
          return {
            title: t("Visitor Mode"),
            message: t(
              "Sign in or create an account to sync your tasks and collaborate with others.",
            ),
            confirmText: t("Sign In"),
            secondaryConfirmText: t("Create Account"),
            actionType: "auth" as const,
          };
        }
        case "connectivity.checking.title": {
          return {
            title: t("Checking Connection"),
            message: t(
              "Verifying your connectivity status. This happens automatically in the background.",
            ),
            hideConfirm: true,
            cancelText: t("Close"),
          };
        }
        case "connectivity.syncing.title": {
          return {
            title: t("Syncing Changes"),
            message: t("Your work is being saved to the cloud."),
            confirmText: t("OK"),
          };
        }
        case "connectivity.pending.title": {
          const countValue = ctx.uploadQueueSize;
          return {
            title: t("Pending Changes"),
            message: t("You have {countValue} changes waiting to be synced.", { countValue }),
            confirmText: t("Sync Now"),
          };
        }
        default: {
          return {
            title: t("You're Connected"),
            message: t("Your data is up to date and synced to the cloud."),
            confirmText: t("Check Again"),
          };
        }
      }
    })();

    return content;
  });

  return dialogContent;
}
